// src/components/ReceiptShiftConfig.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

// format a 24-hour number to “h AM/PM”
const format12h = (h24) => {
  if (h24 == null) return "--";
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12} ${suffix}`;
};

export default function ReceiptShiftConfig() {
  const [shift, setShift] = useState({
    startShiftHour: null,
    endShiftHour: null,
    endShiftNextDay: false,
  });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  // local form state for dialog
  const [form, setForm] = useState({
    start12: 12,
    startAP: "AM",
    end12: 12,
    endAP: "AM",
    nextDay: false,
  });

  // load current shift
  useEffect(() => {
    (async () => {
      try {
        const token = ls.get("authToken");
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/gross-sales-goal`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShift(data.shift);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // open dialog and prefill form
  const handleOpen = () => {
    const { startShiftHour, endShiftHour, endShiftNextDay } = shift;
    const to12 = (h24) => {
      const ap = h24 >= 12 ? "PM" : "AM";
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
      return [h12, ap];
    };
    const [s12, sAP] = to12(startShiftHour ?? 0);
    const [e12, eAP] = to12(endShiftHour ?? 0);
    setForm({
      start12: s12,
      startAP: sAP,
      end12: e12,
      endAP: eAP,
      nextDay: endShiftNextDay,
    });
    setOpen(true);
  };

  // convert back to 0–23
  const to24 = (h12, ap) => {
    if (ap === "AM") return h12 === 12 ? 0 : h12;
    return h12 === 12 ? 12 : h12 + 12;
  };

  const handleSave = async () => {
    const startShiftHour = to24(form.start12, form.startAP);
    const endShiftHour = to24(form.end12, form.endAP);
    const payload = {
      startShiftHour,
      endShiftHour,
      endShiftNextDay: form.nextDay,
    };

    setLoading(true);
    try {
      const token = ls.get("authToken");
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/change-shift`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShift(data.shift);
      setOpen(false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !open) {
    return (
      <Box sx={{ textAlign: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
      }}
    >
      <Tooltip title="Edit opening and closing hours">
        <IconButton color="primary" onClick={handleOpen} size="small">
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Typography variant="h6">
        Day Start: {format12h(shift.startShiftHour)}   Day End:{" "}
        {format12h(shift.endShiftHour)}
        {shift.endShiftNextDay ? " (next day)" : ""}
      </Typography>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Edit Shift Hours</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <TextField
              label="Start Hour"
              type="number"
              inputProps={{ min: 1, max: 12 }}
              value={form.start12}
              onChange={(e) =>
                setForm((f) => ({ ...f, start12: +e.target.value }))
              }
              sx={{ width: 80 }}
            />
            <FormControl sx={{ width: 100 }}>
              <InputLabel>AM/PM</InputLabel>
              <Select
                value={form.startAP}
                label="AM/PM"
                onChange={(e) =>
                  setForm((f) => ({ ...f, startAP: e.target.value }))
                }
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              label="End Hour"
              type="number"
              inputProps={{ min: 1, max: 12 }}
              value={form.end12}
              onChange={(e) =>
                setForm((f) => ({ ...f, end12: +e.target.value }))
              }
              sx={{ width: 80 }}
            />
            <FormControl sx={{ width: 100 }}>
              <InputLabel>AM/PM</InputLabel>
              <Select
                value={form.endAP}
                label="AM/PM"
                onChange={(e) =>
                  setForm((f) => ({ ...f, endAP: e.target.value }))
                }
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* <FormControlLabel
            control={
              <Checkbox
                checked={form.nextDay}
                onChange={(e) =>
                  setForm((f) => ({ ...f, nextDay: e.target.checked }))
                }
              />
            }
            label="End next day?"
          /> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
